// provenance/ProvenanceShow.js
import React, { useState, useEffect } from 'react';
import { Show, useShowContext, TopToolbar, Button, useDataProvider } from 'react-admin';
import { Card, CardContent, Typography, Box, Grid, Chip, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import TokenIcon from '@mui/icons-material/Token';
import { Link as RouterLink } from 'react-router-dom';
import config from '../config/config';

// Import the existing components to reuse
import BoxWine, { defaultWineImage } from './BoxWine';
import BoxCheese, { defaultCheeseImage } from './BoxCheese';
import BoxFlower, { defaultFlowerImage } from './BoxFlower';
import BoxCoffee, { defaultCoffeeImage } from './BoxCoffee';
import BoxSpirit, { defaultSpiritImage } from './BoxSpirit';
import { ProtectedInfo } from './ProvenanceList';
import UIDsList from './UIDsList';
import { mintNFTs} from '../dataProvider'

const ShowActions = () => {
    const { record, isLoading } = useShowContext();
    
    if (isLoading || !record) return null;

    // Use existing fields for now, will be updated by the stats panel
    const uidsCount = record?.uids_count || 0;
    const nftsCount = record?.contract_total_supply || 
                     record?.data?.contract_total_supply || 
                     record?.nfts_count || 
                     0;
    const canMintNFTs = record && uidsCount > nftsCount;
    
    return (
        <TopToolbar>
            <Button
                component="a"
                href={`/api/provenance/${record.id}/download-uids`}
                download
                startIcon={<DownloadIcon />}
                label="Download UIDs"
            />
            {canMintNFTs || (
                <Button
                    startIcon={<TokenIcon />}
                    label="Mint NFTs"
                    onClick={() => {
                        // TODO: Implement mint NFTs functionality
                        console.log('Debugging canMintNFTs:', { uidsCount, nftsCount, record });
                    }}
                />
            )}
        </TopToolbar>
    );
};

const StatsPanel = ({ record, uidsCount }) => {
    const theme = useTheme();
    
    // Check multiple possible paths for NFTs count
    const nftsCount = record?.contract_total_supply || 
                     record?.data?.contract_total_supply || 
                     record?.nfts_count || 
                     0;
    
    console.log('Debug NFTs count:', {
        contract_total_supply: record?.contract_total_supply,
        data_contract_total_supply: record?.data?.contract_total_supply,
        nfts_count: record?.nfts_count,
        final_nftsCount: nftsCount,
        record_keys: Object.keys(record || {}),
        full_record: record
    });
    
    const canMintMore = uidsCount > nftsCount;
    
    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main }}>
                    Product Statistics
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}>
                                {uidsCount}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                NFC Tags (UIDs)
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}>
                                {nftsCount}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                NFTs Minted
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Chip
                                label={canMintMore ? `${uidsCount - nftsCount} Ready to Mint` : 'All NFTs Minted'}
                                color={canMintMore ? 'primary' : 'success'}
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 'bold',
                                    px: 2,
                                    py: 1
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const ProductDetails = ({ record }) => {
    const theme = useTheme();
    
    // Get default image based on product type
    const getDefaultImage = () => {
        switch(record?.data?.productType) {
            case 'cheese':
                return defaultCheeseImage;
            case 'coffee':
                return defaultCoffeeImage;
            case 'flower':
                return defaultFlowerImage;
            case 'spirit':
                return defaultSpiritImage;
            case 'wine':
            default:
                return defaultWineImage;
        }
    };

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, mb: 3 }}>
                    Product Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                    <Box sx={{
                        width: { xs: '100%', sm: 250 },
                        height: { xs: 'auto', sm: 350 },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`
                    }}>
                        <Box
                            component="img"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                maxHeight: { xs: 300, sm: '100%' },
                            }}
                            src={(() => {
                                const imagePath = record?.data?.image || record?.image;
                                if (!imagePath) return getDefaultImage();
                                
                                // If image already includes the full URL (starts with http), use as-is
                                if (imagePath.startsWith('http')) {
                                    return imagePath;
                                }
                                
                                // Otherwise, construct the full URL with S3 base
                                return `${config.s3BaseUrl}/${imagePath}`;
                            })()}
                            alt={record?.data?.name || 'Product'}
                        />
                    </Box>
                    
                    {/* Product specific content - reuse existing components */}
                    {(!record?.data?.productType || record?.data?.productType === 'wine') && <BoxWine record={record.data} />}
                    {record?.data?.productType === 'cheese' && <BoxCheese record={record.data} />}
                    {record?.data?.productType === 'flower' && <BoxFlower record={record.data} />}
                    {record?.data?.productType === 'coffee' && <BoxCoffee record={record.data} />}
                    {record?.data?.productType === 'spirit' && <BoxSpirit record={record.data} />}
                </Box>
            </CardContent>
        </Card>
    );
};

const ProtectedInfoDetails = ({ record }) => {
    const theme = useTheme();
    // Create a normalized record for ProtectedInfo component
    // ProtectedInfo expects data at root level, but ProvenanceShow has it under record.data
    const normalizedRecord = {
        tag_model: record.data?.tag_model,
        is_tag_tamper: record.data?.is_tag_tamper,
        tags_count: record.tags_count, // This might be at root level
        contract_address: record.data?.contract_address
    };
    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main }}>
                    Security & Blockchain Information
                </Typography>
                {/* Reuse the existing ProtectedInfo component */}
                <ProtectedInfo record={normalizedRecord} />
            </CardContent>
        </Card>
    );
};

const ProvenanceShowView = () => {
    const { record, isLoading } = useShowContext();
    const [uidsCount, setUidsCount] = useState(0);
    const [uidsLoading, setUidsLoading] = useState(true);
    const dataProvider = useDataProvider();

    // Fetch UIDs count once per page load
    useEffect(() => {
        const fetchUidsCount = async () => {
            if (!record?.id) return;
            
            try {
                setUidsLoading(true);
                const result = await dataProvider.getList('uids', {
                    pagination: { page: 1, perPage: 1 },
                    filter: { provenance_id: record.id }
                });
                setUidsCount(result.total || 0);
                console.log('UIDs count fetched:', result.total || 0);
            } catch (error) {
                console.error('Error fetching UIDs count:', error);
                setUidsCount(0);
            } finally {
                setUidsLoading(false);
            }
        };

        fetchUidsCount();
    }, [record?.id, dataProvider]);

    if (isLoading || uidsLoading) {
        return <div>Loading...</div>;
    }

    if (!record) {
        return <div>Record not found</div>;
    }
    
    // Don't render the main content until record.data is available
    if (!record.data) {
        return <div>Loading product details...</div>;
    }

    // const uidsCount = Number(record?.uids_count || 0);
    const nftsCount = Number(
        record?.contract_total_supply || 
        record?.data?.contract_total_supply || 
        record?.nfts_count || 
        0
    );

    const canMintNFTs = uidsCount > nftsCount;

    const ShowActions = () => (
        <TopToolbar>
            {/* <Button
                component="a"
                href={`/api/provenance/${record.id}/download-uids`}
                download
                startIcon={<DownloadIcon />}
                label="Download UIDs"
            /> */}
            {canMintNFTs && (
                <Button
                    startIcon={<TokenIcon />}
                    label="Mint NFTs"
                    onClick={() => {
                        console.log('Mint NFTs for:', record.id);
                        mintNFTs();
                    }}
                />
            )}
        </TopToolbar>
    );

    return (
        <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
            <ShowActions />
            <Box sx={{ p: { xs: 1, sm: 3 } }}>
                <StatsPanel record={record} uidsCount={uidsCount} />
                <ProductDetails record={record} />
                <ProtectedInfoDetails record={record} />
                <UIDsList provenanceId={record.id} />
            </Box>
        </Box>
    );
};

export const ProvenanceShow = () => (
    // <Show actions={<ShowActions />}>
    <Show actions="">
        <ProvenanceShowView />
    </Show>
);

export default ProvenanceShow;